/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable jsx-a11y/accessible-emoji */
import React, { useState, useEffect, useCallback } from 'react';
import queryString from 'query-string';
import { useLocation, useHistory } from 'react-router';
import { ChevronDown, ChevronUp, Star } from '@saleshandy/icons';
import type { IProps } from './outreach-plans-container';
import BillingSummaryModal from './components/billing-summary-modal';
import PaddleBillingSummaryModal from './components/paddle-billing-summary-modal/paddle-billing-summary-modal';
import { previewPaddleSubscription, getUpdatePaymentMethodTransaction } from '../../helpers/billing-subscription.api';
import usePaddleCustomerAuthToken from '../../../../../../shared/hooks/use-paddle-customer-auth-token';
import type { PaddlePreviewSubscriptionResponse } from '../../types/paddle';
import {
  executeOnErrorWithErrorCheck,
  executeOnRequestStatus,
} from '../../../../../../shared/utils/execute-on-request-status';
import { showGeneralErrorNotification } from '../../../../../../shared/utils/errors';
import { getIsUserSubscribed, isRequestPending } from '../../utils/helper';
import PlansSection from './components/plans-section';
import { accessibleOnClick } from '../../../../../../shared/utils/accessible-on-click';
import { BillingOption } from '../../../../../../shared/utils/billing-options';
import { getDisabledLabel } from '../modify-subscription/utils/helper';
import { Plan } from '../../types';
import {
  SubscriptionPlans,
  SubscriptionPlanTitle,
} from '../../../../../../shared/utils/subscription-plans';
import {
  getIsRequestPending,
  redirectWithoutRefresh,
} from '../../../../../../shared/utils';
import { showToastMessage } from '../../../../../../shared/design-system/components/molecules/upgrade-plan-billing-section/helper/helper';
import { supportUrls } from '../../../../../../shared/utils/urls';
import {
  getPlanIndex,
  getPlanPeriod,
  getSubscribeAction,
  UpgradePlanQueryParamsKey,
  isValueBasedPlan,
} from './utils/helper';
import { getPlanId } from '../upgrade-plan/utils/helper';
import { openChatSupport } from '../../../../../../shared/utils/open-chat-support';
import { Routes } from '../../../../../../shared/routes';
import PlanCycleSwitch from '../../../../../../shared/design-system/components/molecules/plan-cycle-switch';
import FeaturesHeader from '../cold-email-plans/components/cold-email-plans-comparison/components/features-header';
import FeaturesSection from '../cold-email-plans/components/cold-email-plans-comparison/components/features-section';
import { getAllFeaturesList } from '../cold-email-plans/components/cold-email-plans-comparison/utils/get-all-features';
import { ColdEmailPlanName } from '../cold-email-plans/components/cold-email-plan-card/types';
import {
  getFestiveDiscountCouponCode,
  getIsFestiveDiscountActive,
  getOutreachPlanMetaData,
} from './components/outreach-plan-card/utils/helper';
import UpgradePlanStatic from '../upgrade-plan-static/upgrade-plan-static';
import { getCustomScalePlusPlanData } from './components/plans-section/utils/helper';
import { getPlanMode } from '../select-plan/utils/helper';
import toaster from '../../../../../../shared/toaster';
import usePaddleIntegration from '../../../../../../shared/hooks/script-integrations/usePaddleIntegration';
import usePaddleCoupon from '../../hooks/use-paddle-coupon';
import { ShAccountSettingsCode } from '../../../../enums/admin-settings';

const MANUALLY_MODIFY_SUBSCRIPTION_MSG =
  'Your subscription was created manually. Please contact the support team to modify your subscription.';

const OutreachPlans: React.FC<IProps> = ({
  //* API Request State
  getCouponCodeResponse,
  sendGetAccountSubscriptionRequestStatus,
  sendGetAccountSubscriptionResponse,
  sendGetPlansResponse,
  sendPurchaseSubscriptionRequestStatus,
  sendPurchaseSubscriptionRequestMessage,
  sendGetPurchaseModifySubscriptionRequestStatus,
  resetPurchaseModifySubscriptionResponse,
  resetPurchaseSubscriptionRequestStatus,
  sendPurchaseModifySubscriptionResponse,
  //* API Request
  sendPurchaseSubscriptionRequest,
  sendPurchaseModifySubscriptionRequest,
  sendGetCalculatePayRequest,
  sendGetAccountSubscriptionRequest,
  sendGetUserSettingsRequest,
  sendGetUserSubscriptionMetadataRequest,
  resetSubscription,

  sendDynamicCustomPlansRequest,

  schedulePlanUpgradeRequestStatus,
  schedulePlanUpgradeRequestError,
  sendSchedulePlanUpgradeRequest,
  resetSchedulePlanUpgradeRequest,
  updateIsPlanUpgradeScheduled,

  paymentProvider,
  userId,
  shAccountId,
  userEmail,
  paddlePolling,
  startPaddlePollingThunk,
  adminSettings,
  paddleCustomerId,
}) => {
  const location = useLocation();
  const history = useHistory();

  const isFestiveDiscountActive = getIsFestiveDiscountActive();
  const festiveDiscountCouponCode = getFestiveDiscountCouponCode();

  //* States
  const [planCycle, setPlanCycle] = useState<BillingOption>(
    BillingOption.ANNUALLY,
  );
  const isPurchaseInProgress =
    isRequestPending(sendPurchaseSubscriptionRequestStatus) ||
    isRequestPending(sendGetPurchaseModifySubscriptionRequestStatus);
  const [
    isBillingSummaryModalShow,
    setIsBillingSummaryModalShow,
  ] = useState<boolean>(false);
  const [isUserModifyingSubscription, setIsModifySubscription] = useState(
    false,
  );
  const [
    isAllFeaturesTableVisible,
    setIsAllFeaturesTableVisible,
  ] = useState<boolean>(false);
  const [selectedPlanDetails, setSelectedPlanDetails] = useState<Plan>();

  const [
    isPaddleBillingSummaryModalShow,
    setIsPaddleBillingSummaryModalShow,
  ] = useState(false);
  const [
    paddlePreviewData,
    setPaddlePreviewData,
  ] = useState<PaddlePreviewSubscriptionResponse | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPaymentMethodUpdate, setIsPaymentMethodUpdate] = useState(false);
  const [isUpdatingPaymentMethod, setIsUpdatingPaymentMethod] = useState(false);

  const { getToken: getCustomerAuthToken } = usePaddleCustomerAuthToken(paddleCustomerId);

  const {
    isCouponApplying,
    isCouponApplied,
    isCouponInvalid,
    preAppliedCouponCode,
    setPreAppliedCouponCode,
    onApplyCoupon: onPaddleApplyCoupon,
    onClearCoupon: onPaddleClearCoupon,
    resetCouponState,
  } = usePaddleCoupon({
    planId: selectedPlanDetails?.id,
    paddlePriceId: selectedPlanDetails?.paddlePlanId,
    application: 'sequence',
    shAccountId,
    onPreviewUpdate: setPaddlePreviewData,
  });

  const isFreePlanPaddleUser =
    !isUserModifyingSubscription && paymentProvider === 'paddle';

  const OUTREACH_SUBSCRIPTION_URL = '/billing/subscriptions/email-outreach';

  const isPaddlePolling = paddlePolling?.isActive && paddlePolling?.applicationType === 'sequence';

  const handleCheckoutStateReset = useCallback(() => {
    setIsPaddleBillingSummaryModalShow(false);
    setPaddlePreviewData(null);
    resetCouponState();
  }, [resetCouponState]);

  const { isReady: isPaddleReady, openCheckout } = usePaddleIntegration({
    onCheckoutComplete: (data) => {
      if (isPaymentMethodUpdate) {
        const payment = data?.data?.payment;
        const card = payment?.method_details?.card;
        if (card && paddlePreviewData) {
          setPaddlePreviewData({
            ...paddlePreviewData,
            payload: {
              ...paddlePreviewData.payload,
              cardDetails: [{
                paymentMethodId: payment.payment_method_id || '',
                type: payment.method_details?.type || 'card',
                card: {
                  type: card.type || '',
                  last4: card.last4 || '',
                  expiryMonth: card.expiry_month,
                  expiryYear: card.expiry_year,
                  cardholderName: card.cardholder_name || '',
                },
              }],
            },
          });
        }
        if (window.Paddle?.Checkout?.close) {
          window.Paddle.Checkout.close();
        }
        return;
      }
      if (window.Paddle?.Checkout?.close) {
        window.Paddle.Checkout.close();
      }
      handleCheckoutStateReset();
      redirectWithoutRefresh(OUTREACH_SUBSCRIPTION_URL);
      startPaddlePollingThunk({
        shAccountId,
        applicationType: 'sequence',
        planId: selectedPlanDetails?.id,
      });
    },
    onCheckoutError: () => {
      handleCheckoutStateReset();
    },
    onCheckoutPaymentError: () => {
      handleCheckoutStateReset();
      toaster.error('Payment failed. Please try again.');
      redirectWithoutRefresh(OUTREACH_SUBSCRIPTION_URL);
    },
    onError: () => {
      toaster.error('Something went wrong. Please try again.');
    },
    onCheckoutClosed: () => {
      if (isPaymentMethodUpdate) {
        setIsPaymentMethodUpdate(false);
        return;
      }
      handleCheckoutStateReset();
    },
  });

  const toggleAllFeaturesTable = () =>
    setIsAllFeaturesTableVisible(!isAllFeaturesTableVisible);

  const showBillingSummaryModal = () => setIsBillingSummaryModalShow(true);
  const hideBillingSummaryModal = () => {
    setIsBillingSummaryModalShow(false);
    resetQueryParams();
    resetCouponState();
  };

  const handelBillingOption = (value) => setPlanCycle(value);

  const [planDetails] = useState<Plan[]>(sendGetPlansResponse);

  const [dynamicPlanAmount, setDynamicPlanAmount] = useState<number | null>(
    null,
  );

  const onSelectPlan = async (
    planId: number,
    planName: string,
    counts?: { prospectsCount: number; emailsCount: number },
  ) => {
    if (isPaddlePolling) return;
    const { planCode } = sendGetAccountSubscriptionResponse;

    if (planName === SubscriptionPlanTitle.Custom) {
      openChatSupport();
      return;
    }
    const selectedPlan = sendGetPlansResponse.find(({ id }) => id === planId);
    setSelectedPlanDetails(selectedPlan);

    let effectivePlan = selectedPlan;

    const customScalePlusPlanIds = getCustomScalePlusPlanData(
      getPlanMode(planCycle),
      sendGetPlansResponse,
    )?.map((plan) => plan?.id);

    const isCustomScalePlusPlan = customScalePlusPlanIds?.includes(planId);

    if (isCustomScalePlusPlan && counts) {
      const payload = {
        basePlanId: planId,
        prospectCount: counts?.prospectsCount,
        emailCount: counts?.emailsCount,
      };

      try {
        const dynamicPlansResponse = await sendDynamicCustomPlansRequest(
          payload,
        );
        const dynamicPlanId = dynamicPlansResponse?.payload?.payload?.id;
        const amount = dynamicPlansResponse?.payload?.payload?.amount;
        const dynamicPaddlePriceId =
          dynamicPlansResponse?.payload?.payload?.paddlePlanId;
        setDynamicPlanAmount(amount);

        if (dynamicPlanId) {
          planId = dynamicPlanId;
          const dynamicSelectedPlan = {
            ...selectedPlan,
            id: dynamicPlanId,
            ...(dynamicPaddlePriceId && {
              paddlePlanId: dynamicPaddlePriceId,
            }),
          };
          setSelectedPlanDetails(dynamicSelectedPlan);
          effectivePlan = dynamicSelectedPlan;
        } else {
          setSelectedPlanDetails(selectedPlan);
        }
      } catch (error) {
        return;
      }
    }

    if (getIsUserSubscribed(planCode) && paymentProvider !== 'paddle') {
      sendGetCalculatePayRequest({
        accountSubscriptionPlanId: planId,
        slots: 1,
      });
    }

    if (isFreePlanPaddleUser && effectivePlan?.paddlePlanId && isPaddleReady) {
      const toltReferral = adminSettings?.find(
        (s) => s.code === ShAccountSettingsCode.RewardfulReferralId,
      )?.value;
      const celloUcc = adminSettings?.find(
        (s) => s.code === ShAccountSettingsCode.CelloReferralCode,
      )?.value;
      const authToken = await getCustomerAuthToken();
      openCheckout({
        items: [{ priceId: effectivePlan.paddlePlanId, quantity: 1 }],
        ...(authToken && { customerAuthToken: authToken }),
        customer: {
          ...(paddleCustomerId
            ? { id: paddleCustomerId }
            : { email: userEmail }),
        },
        customData: {
          user_id: userId,
          sh_account_id: shAccountId,
          ...(toltReferral && { tolt_referral: toltReferral }),
          ...(celloUcc && { cello_ucc: celloUcc }),
        },
        settings: {
          displayMode: 'overlay',
          variant: 'one-page',
          theme: 'light',
          ...(paddleCustomerId && { allowLogout: false }),
        },
      });
      return;
    }

    const couponCodeToApply =
      isFestiveDiscountActive &&
        festiveDiscountCouponCode &&
        planCycle === BillingOption.ANNUALLY
        ? festiveDiscountCouponCode
        : '';

    if (couponCodeToApply) {
      setPreAppliedCouponCode(couponCodeToApply);
    }

    if (paymentProvider === 'paddle') {
      setIsPreviewLoading(true);
      setIsPaddleBillingSummaryModalShow(true);
      try {
        const response = await previewPaddleSubscription({
          accountSubscriptionPlanId: planId,
          shAccountId,
          application: 'sequence',
          ...(couponCodeToApply && { couponCode: couponCodeToApply }),
        });
        setPaddlePreviewData(response);
      } catch {
        toaster.error('Failed to load billing preview. Please try again.');
        setIsPaddleBillingSummaryModalShow(false);
      } finally {
        setIsPreviewLoading(false);
      }
    } else {
      showBillingSummaryModal();
    }
    updateQueryParams(planId);
  };

  const onPurchase = (token, couponCode) => {
    if (!isUserModifyingSubscription) {
      const payload = {
        slots: 1,
        planId: Number(selectedPlanDetails?.id),
        ...(token && { stripeCardToken: token.id }),
        ...(getCouponCodeResponse &&
          !getCouponCodeResponse.isError &&
          couponCode && { couponCode }),
      };
      sendPurchaseSubscriptionRequest(payload);
    } else {
      const { customerId, subscriptionId } = sendGetAccountSubscriptionResponse;
      if (!customerId || !subscriptionId) {
        showGeneralErrorNotification(MANUALLY_MODIFY_SUBSCRIPTION_MSG);
        return;
      }
      const payload = {
        accountSubscriptionPlanId: Number(selectedPlanDetails?.id),
        slots: 1,
        stripeCardToken: token.id,
        ...(getCouponCodeResponse &&
          !getCouponCodeResponse.isError &&
          couponCode && { couponCode }),
      };
      sendPurchaseModifySubscriptionRequest(payload);
    }
  };

  const onPaddlePayNow = () => {
    if (!paddleCustomerId) {
      showGeneralErrorNotification(MANUALLY_MODIFY_SUBSCRIPTION_MSG);
      return;
    }
    sendPurchaseModifySubscriptionRequest({
      accountSubscriptionPlanId: Number(selectedPlanDetails?.id),
      ...(preAppliedCouponCode && { couponCode: preAppliedCouponCode }),
    });
  };

  const onUpdateCardDetails = async () => {
    const { customerId, subscriptionId } = sendGetAccountSubscriptionResponse;
    if (!customerId || !subscriptionId) {
      showGeneralErrorNotification(MANUALLY_MODIFY_SUBSCRIPTION_MSG);
      return;
    }
    const authToken = await getCustomerAuthToken();  
    try {
      setIsUpdatingPaymentMethod(true);
      const response = await getUpdatePaymentMethodTransaction(subscriptionId);
      const transactionId = (response as any)?.payload?.transactionId;
      if (!transactionId) return;

      setIsPaymentMethodUpdate(true);
      openCheckout({
        transactionId,
        ...(authToken && { customerAuthToken: authToken }),
        settings: { displayMode: 'overlay', theme: 'light', variant:'one-page' },
      });
    } catch {
      toaster.error('Failed to update payment method. Please try again.');
    } finally {
      setIsUpdatingPaymentMethod(false);
    }
  };

  const hidePaddleBillingSummaryModal = () => {
    setIsPaddleBillingSummaryModalShow(false);
    setPaddlePreviewData(null);
    resetQueryParams();
    resetCouponState();
  };

  const sendPurchaseSubscriptionRequestSuccess = (message) => {
    showToastMessage(message);

    sendGetAccountSubscriptionRequest();
    redirectWithoutRefresh('/billing/subscriptions?plan=subscribed');
    resetSubscription();

    setTimeout(() => {
      // Stripe is taking time to update payment is pending or not data
      sendGetUserSettingsRequest();
      sendGetUserSubscriptionMetadataRequest();
    }, 500);
  };

  const updateQueryParams = (planId) => {
    const queryParams = new URLSearchParams(location.search);
    const index = getPlanIndex(sendGetPlansResponse, planId);
    const selectPlanIndex = index < 3 ? index + 1 : index - 2;

    queryParams.set(
      UpgradePlanQueryParamsKey.Action,
      getSubscribeAction(isUserModifyingSubscription),
    );
    queryParams.set(UpgradePlanQueryParamsKey.Period, getPlanPeriod(planCycle));
    queryParams.set(UpgradePlanQueryParamsKey.Plan, selectPlanIndex.toString());

    history.push({
      pathname: Routes.Setting_Billing_Upgrade_Email_Outreach_Plan,
      search: queryParams.toString(),
    });
  };

  const resetQueryParams = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete(UpgradePlanQueryParamsKey.Action);
    queryParams.delete(UpgradePlanQueryParamsKey.Period);
    queryParams.delete(UpgradePlanQueryParamsKey.Plan);

    history.push({
      pathname: Routes.Setting_Billing_Upgrade_Email_Outreach_Plan,
      search: queryParams.toString(),
    });
  };

  const selectPlanBasedOnUrl = () => {
    const { planName, planType, planCode } = sendGetAccountSubscriptionResponse;
    const params = queryString.parse(location.search);
    const { plan, period } = params;

    if (sendGetPlansResponse) {
      const upgradePlan = sendGetPlansResponse.filter(
        ({ planMode, planType: type }) =>
          Number(period) === planMode && isValueBasedPlan(type),
      )[Number(plan) - 1];

      const subscriptionId = getPlanId(
        sendGetPlansResponse,
        planName,
        planCode,
        planType,
      );

      if (
        upgradePlan?.id &&
        (upgradePlan?.id > subscriptionId ||
          planCode === SubscriptionPlans.FREE ||
          planCode === SubscriptionPlans.TRIAL)
      ) {
        onSelectPlan(upgradePlan?.id, upgradePlan?.planName);
      }
    }
  };

  const getActivePlanName = () => {
    const { planName } = sendGetAccountSubscriptionResponse;
    const emailOutreachPlanMetaData = getOutreachPlanMetaData(planName);

    return emailOutreachPlanMetaData?.displayName || planName;
  };

  const onSchedule = () => {
    sendSchedulePlanUpgradeRequest();
  };

  // const getPremiumFeatureTooltipText = (): string =>
  //   'A/Z variant \nPipedrive Integration \nTeam Management \nAPI \nSender Rotation \nUnified Inbox Reply';

  //* useEffect
  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetAccountSubscriptionRequestStatus,
      onSuccess: () => {
        const { planCode } = sendGetAccountSubscriptionResponse;

        const isUserSubscribed = getIsUserSubscribed(planCode);
        setIsModifySubscription(isUserSubscribed);

        selectPlanBasedOnUrl();
      },
    });
  }, [sendGetAccountSubscriptionRequestStatus]);

  // Purchase subscription
  useEffect(() => {
    executeOnRequestStatus({
      status: sendPurchaseSubscriptionRequestStatus,
      onSuccess: () => {
        sendPurchaseSubscriptionRequestSuccess(
          sendPurchaseSubscriptionRequestMessage,
        );
        resetPurchaseSubscriptionRequestStatus();
      },
    });
  }, [sendPurchaseSubscriptionRequestStatus]);

  // Modify subscription
  useEffect(() => {
    executeOnRequestStatus({
      status: sendGetPurchaseModifySubscriptionRequestStatus,
      onSuccess: () => {
        if (paymentProvider === 'paddle') {
          hidePaddleBillingSummaryModal();
          redirectWithoutRefresh(OUTREACH_SUBSCRIPTION_URL);
          startPaddlePollingThunk({
            shAccountId,
            applicationType: 'sequence',
            planId: selectedPlanDetails?.id,
          });
        } else {
          sendPurchaseSubscriptionRequestSuccess(
            sendPurchaseModifySubscriptionResponse?.message,
          );
        }
        resetPurchaseModifySubscriptionResponse();
      },
    });
  }, [sendGetPurchaseModifySubscriptionRequestStatus]);

  // Schedule Plan Request
  useEffect(() => {
    executeOnRequestStatus({
      status: schedulePlanUpgradeRequestStatus,
      onSuccess: () => {
        toaster.success(
          'Your account has been modified successfully. The new feature will be available at the next billing cycle.',
        );
        history.push('/billing/subscriptions/email-outreach');
        resetSchedulePlanUpgradeRequest();
        updateIsPlanUpgradeScheduled();
      },
      onFailed: () => {
        resetSchedulePlanUpgradeRequest();
        executeOnErrorWithErrorCheck({
          error: schedulePlanUpgradeRequestError,
          onError: () => {
            toaster.error(schedulePlanUpgradeRequestError.message);
          },
        });
      },
    });
  }, [schedulePlanUpgradeRequestStatus]);

  return (
    <div className="outreach-plans">
      <div className="outreach-plans-header">
        <h1>Find the right Email Outreach Plan</h1>
        <span>
          You are currently on the{' '}
          <span className="semibold-1">{getActivePlanName()} plan</span>
        </span>
      </div>

      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ background: '#f9fafb', height: '43px', paddingTop: '18px' }}
      >
        <div className="d-flex flex-column align-items-end mt-4">
          <PlanCycleSwitch
            value={planCycle}
            onChange={handelBillingOption}
            disabledLabel={getDisabledLabel(sendGetAccountSubscriptionResponse)}
          />
        </div>
      </div>

      <div className="d-flex justify-content-center">
        <PlansSection
          onSelectPlan={onSelectPlan}
          planCycle={planCycle}
          selectedPlanId={selectedPlanDetails?.id}
          onSchedule={onSchedule}
          isScheduleRequestLoading={getIsRequestPending(
            schedulePlanUpgradeRequestStatus,
          )}
          isPaddlePolling={isPaddlePolling}
        />
      </div>

      <div className="informational-note-container">
        <div className="informational-note">
          <p className="note-label">Note:</p>
          <p>
            The savings above show how much you can save by choosing to pay for
            the entire year instead of paying monthly.
          </p>
        </div>
      </div>

      <div
        className="d-flex justify-content-center align-items-center mt-4 pointer compare-plan-expand"
        {...accessibleOnClick(toggleAllFeaturesTable)}
      >
        <span className="compare-text pointer">
          Compare Plans & See all features
        </span>
        {isAllFeaturesTableVisible ? (
          <ChevronUp className="chevron-down-icon" />
        ) : (
          <ChevronDown className="chevron-down-icon" />
        )}
      </div>

      {isAllFeaturesTableVisible && (
        <div className="cold-email-plan-comparison all-features-list-modal">
          <div className="features-table">
            {ColdEmailPlanName.Professional && (
              <div className="d-flex justify-content-center best-value-plan__label-wrapper">
                <span className="d-flex justify-content-center font-weight-bold bs-tag bs-tag-blue-3 most-popular-plan">
                  <Star className="mr-1" />
                  <span className="font-8">MOST POPULAR</span>
                </span>
              </div>
            )}
            <FeaturesHeader
              planDetailObject={planDetails}
              planCycle={planCycle}
              handlePlanSelect={(planId, planName, counts) =>
                onSelectPlan(planId, planName, counts)
              }
              sendGetAccountSubscriptionResponse={
                sendGetAccountSubscriptionResponse
              }
              sendGetPlansResponse={sendGetPlansResponse}
              isPurchaseInProgress={isPurchaseInProgress || isPaddlePolling}
              onSchedule={onSchedule}
              isScheduleRequestLoading={getIsRequestPending(
                schedulePlanUpgradeRequestStatus,
              )}
            />
            <div className="features-content">
              {getAllFeaturesList().map((item) => (
                <FeaturesSection
                  key={item.id}
                  id={item.id}
                  title={item.title}
                  features={item.features}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="secondary-banner-container">
        <UpgradePlanStatic link={supportUrls.outreachPlanViewFAQ} />
      </div>

      <BillingSummaryModal
        show={isBillingSummaryModalShow}
        onClose={hideBillingSummaryModal}
        selectedPlanDetails={selectedPlanDetails}
        onPurchase={onPurchase}
        isUserModifyingSubscription={isUserModifyingSubscription}
        isAnnuallyCycle={planCycle === BillingOption.ANNUALLY}
        dynamicAmount={dynamicPlanAmount}
        setDynamicAmount={setDynamicPlanAmount}
        preAppliedCouponCode={preAppliedCouponCode}
      />

      <PaddleBillingSummaryModal
        show={isPaddleBillingSummaryModalShow}
        onClose={hidePaddleBillingSummaryModal}
        planName={selectedPlanDetails?.planName || ''}
        previewData={paddlePreviewData}
        onPayNow={onPaddlePayNow}
        isPurchaseInProgress={isPurchaseInProgress}
        isPreviewLoading={isPreviewLoading}
        onUpdateCardDetails={onUpdateCardDetails}
        isUpdatingPaymentMethod={isUpdatingPaymentMethod}
        onApplyCoupon={onPaddleApplyCoupon}
        onClearCoupon={onPaddleClearCoupon}
        isCouponApplying={isCouponApplying}
        isCouponApplied={isCouponApplied}
        isCouponInvalid={isCouponInvalid}
      />
    </div>
  );
};

export default OutreachPlans;
